import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Home, LifeBuoy, PackageSearch, Search } from 'lucide-react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import SEO from '@/components/SEO';
import { ROUTES } from '@/config/routes';

const NotFound = () => {
  const location = useLocation();
  const attemptedPath = location.pathname + location.search;

  const suggestions = [
    { to: ROUTES.HOME, icon: Home, label: 'Home' },
    { to: ROUTES.TRACK_ORDER, icon: PackageSearch, label: 'Track Order' },
    { to: ROUTES.HELP_CENTER, icon: LifeBuoy, label: 'Help Center' },
    { to: ROUTES.CONTACT, icon: Search, label: 'Contact Us' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page is intentionally excluded from search indexes. */}
      <SEO
        title="Page Not Found"
        description="The page you were looking for does not exist on Boxd Logistics. Head back to the homepage or explore tracking, help, and contact options."
        robots="noindex, follow"
      />
      <Navbar />

      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-gray-50 pt-32 pb-20 px-4">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(59, 130, 246, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.04) 1px, transparent 1px)',
            backgroundSize: '52px 52px'
          }}
        />
        <div className="absolute -top-16 -right-10 h-72 w-72 rounded-full bg-blue-200/30 blur-3xl" />
        <div className="absolute -bottom-20 -left-10 h-80 w-80 rounded-full bg-blue-100/40 blur-3xl" />

        <div className="relative max-w-5xl mx-auto text-center">
          <span className="inline-flex items-center rounded-full border border-blue-200 bg-white/80 px-4 py-1.5 text-sm font-semibold text-blue-900 shadow-sm">
            Error 404
          </span>

          <h1 className="mt-6 text-6xl md:text-8xl font-extrabold leading-none tracking-tight text-gray-900">
            <span className="bg-gradient-to-r from-blue-700 via-blue-800 to-blue-950 bg-clip-text text-transparent">
              404
            </span>
          </h1>

          <h2 className="mt-4 text-2xl md:text-4xl font-bold text-gray-900">
            This page took a wrong turn
          </h2>
          <p className="mt-4 text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            We couldn&apos;t find the page you were looking for. It might have been
            moved, renamed, or never existed. Let&apos;s get you back on route.
          </p>

          {attemptedPath && attemptedPath !== '/' && (
            <p className="mt-3 text-sm text-gray-500">
              Requested URL: <code className="rounded bg-gray-100 px-2 py-0.5 text-gray-700">{attemptedPath}</code>
            </p>
          )}

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-800 to-blue-900 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-800/20 transition hover:from-blue-900 hover:to-blue-950"
            >
              <Home className="h-4 w-4" />
              Back to Home
            </Link>
            <button
              type="button"
              onClick={() => window.history.back()}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-6 py-3 text-sm font-medium text-gray-700 transition hover:border-blue-300 hover:text-blue-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Go Back
            </button>
          </div>
        </div>
      </section>

      <section className="px-4 pb-20">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-center text-sm font-semibold uppercase tracking-widest text-gray-500 mb-6">
            Try one of these instead
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {suggestions.map(({ to, icon: Icon, label }) => (
              <Link
                key={to}
                to={to}
                className="group flex flex-col items-center justify-center gap-3 rounded-2xl border border-gray-200 bg-white px-5 py-6 text-center shadow-sm transition hover:border-blue-300 hover:shadow-md"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-700 to-blue-800 text-white transition group-hover:scale-105">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="text-sm font-semibold text-gray-800 group-hover:text-blue-900">
                  {label}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NotFound;
